import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import {
  FormInput,
  FormTextarea,
  FormSelect,
  FormButton,
} from "./FormComponents";
import TrackerInfo from "./TrackerInfo";
import DataTypeExamples from "./DataTypeExamples";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  updateTrackerAsync,
  deleteTrackerAsync,
} from "../store/slices/trackersSlice";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import { DayOfWeek } from "../types";
import AppSidebar from "./AppSidebar";
import { useTheme } from "../hooks/useTheme";

const EditTracker: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { isDark, setTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const userProfile = useAppSelector((state) => state.user.profile);
  const initials = userProfile?.name
    ? userProfile.name
        .split(" ")
        .map((w: string) => w.charAt(0).toUpperCase())
        .slice(0, 2)
        .join("")
    : (userProfile?.username?.charAt(0).toUpperCase() ?? "U");

  const handleThemeToggle = () => setTheme(isDark ? "light" : "dark");

  const isNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const mobileNavLinks = [
    { path: "/app", emoji: "📅", label: t("nav.calendar") },
    { path: "/habits", emoji: "🔥", label: t("nav.habits") },
    { path: "/todos", emoji: "✅", label: t("nav.todos") },
    { path: "/trackers", emoji: "📊", label: t("nav.trackers") },
    { path: "/notes", emoji: "📝", label: t("nav.notes") },
  ];

  // Get the tracker from Redux state
  const tracker = useAppSelector((state) =>
    state.trackers.items.find((t) => t.id === id),
  );

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    dataType: "" as "number" | "string" | "boolean" | "time" | "options" | "",
    unit: "",
    options: [] as string[],
    trackingDays: [] as DayOfWeek[],
    isPaused: false,
  });

  // Load tracker data when component mounts
  useEffect(() => {
    trackPageVisit("edit_tracker");
    if (tracker) {
      setFormData({
        name: tracker.name,
        description: tracker.description || "",
        dataType: tracker.dataType,
        unit: tracker.unit || "",
        options: tracker.options || [],
        trackingDays: tracker.trackingDays || [],
        isPaused: tracker.isPaused || false,
      });
    }
  }, [tracker]);

  // Track original options count to distinguish new vs existing
  const originalOptionsCount = tracker?.options?.length || 0;

  const dataTypeOptions = t("editTracker.dataTypeOptions", {
    returnObjects: true,
  }) as { value: string; label: string }[];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.dataType || !id) {
      return;
    }

    // Validate options for 'options' dataType
    if (
      formData.dataType === "options" &&
      (!formData.options ||
        formData.options.length === 0 ||
        formData.options.some((opt) => !opt.trim()))
    ) {
      return;
    }

    setLoading(true);

    try {
      // Dispatch updateTrackerAsync action to Redux store
      await dispatch(
        updateTrackerAsync({
          id,
          updates: {
            name: formData.name.trim(),
            dataType: formData.dataType,
            description: formData.description.trim() || undefined,
            unit: formData.unit.trim() || undefined,
            options:
              formData.dataType === "options"
                ? formData.options.filter((opt) => opt.trim())
                : undefined,
            trackingDays:
              formData.trackingDays.length > 0
                ? formData.trackingDays
                : undefined,
            isActive: true,
            isPaused: formData.isPaused,
          },
        }),
      );

      // Track tracker editing event
      trackUserInteraction("edit_tracker", "tracker_management", id);

      // Navigate to trackers page
      navigate("/trackers");
    } catch (error) {
      console.error("Error updating tracker:", error);
      // TODO: Show error message to user
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    setDeleteLoading(true);

    try {
      // Track tracker deletion event
      trackUserInteraction("delete_tracker", "tracker_management", id);

      await dispatch(deleteTrackerAsync(id));
      navigate("/trackers");
    } catch (error) {
      console.error("Error deleting tracker:", error);
      // TODO: Show error message to user
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleCancel = () => {
    navigate("/trackers");
  };

  const showUnitField = formData.dataType === "number";

  // Show loading if tracker is not loaded yet
  if (!tracker) {
    return (
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-14 h-14 bg-[#a33fe8]/20 border border-[#a33fe8]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl animate-spin">⏳</span>
          </div>
          <h2 className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white mb-1">
            {t("editTracker.loadingTracker")}
          </h2>
          <p className="text-[17px] text-gray-500 dark:text-white/40">
            {t("editTracker.loadingTrackerDesc")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <MetaHead
        title={t("editTracker.title")}
        description={t("editTracker.description") as string}
        keywords={t("editTracker.keywords") as string}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#a33fe8]/20 border border-[#a33fe8]/30 flex items-center justify-center transition-all hover:bg-[#a33fe8]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#a33fe8"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("editTracker.headerTitle")}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-[19px] flex items-center justify-center cursor-pointer transition-all"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#a33fe8]/15 border border-[#a33fe8]/25 flex items-center justify-center transition-all hover:bg-[#a33fe8]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#a33fe8"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {t("editTracker.headerTitle")}
            </h1>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            <div className="max-w-3xl mx-auto pb-8 space-y-4">
              <div className="rounded-xl border border-[#a33fe8]/20 dark:border-[#a33fe8]/30 bg-gradient-to-br from-[#a33fe8]/[0.07] to-transparent dark:from-[#a33fe8]/[0.09] p-4 lg:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#a33fe8]/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[24px] leading-none">📊</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[19px] font-semibold tracking-tight text-gray-900 dark:text-white truncate">
                        {tracker.name}
                      </p>
                      <p className="text-[16px] text-gray-600 dark:text-white/45">
                        {t("editTracker.dataType")}: {formData.dataType}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`text-[15px] font-semibold px-2.5 py-1 rounded-full shrink-0 ${formData.isPaused ? "bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30" : "bg-[#22c67a]/20 text-[#22c67a] border border-[#22c67a]/30"}`}
                  >
                    {formData.isPaused
                      ? t("editTracker.trackerPaused")
                      : t("editTracker.trackerActive")}
                  </span>
                </div>
              </div>

              <div className="">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                      <FormInput
                        id="name"
                        label={t("editTracker.trackerName") as string}
                        value={formData.name}
                        onChange={(value) =>
                          setFormData({ ...formData, name: value })
                        }
                        placeholder={
                          t("editTracker.trackerNamePlaceholder") as string
                        }
                        required
                      />
                    </div>

                    <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                      <FormSelect
                        id="dataType"
                        label={t("editTracker.dataType") as string}
                        value={formData.dataType}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            dataType: value as
                              | "number"
                              | "string"
                              | "boolean"
                              | "time"
                              | "options",
                          })
                        }
                        options={dataTypeOptions}
                        placeholder={
                          t("editTracker.dataTypePlaceholder") as string
                        }
                        required
                        disabled={true}
                      />
                      <p className="text-[15px] text-gray-400 dark:text-white/30 mt-1">
                        {t("editTracker.dataTypeNote")}
                      </p>
                    </div>
                  </div>

                  {showUnitField && (
                    <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                      <FormInput
                        id="unit"
                        label={t("editTracker.unit") as string}
                        value={formData.unit}
                        onChange={(value) =>
                          setFormData({ ...formData, unit: value })
                        }
                        placeholder={t("editTracker.unitPlaceholder") as string}
                      />
                    </div>
                  )}

                  {formData.dataType === "options" && (
                    <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                      <label className="block text-[17px] font-semibold text-gray-800 dark:text-white/80 mb-2">
                        {t("editTracker.options")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2">
                        {formData.options.map((option, index) => {
                          const isExistingOption = index < originalOptionsCount;
                          return (
                            <div
                              key={index}
                              className="flex items-center gap-2"
                            >
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => {
                                  const newOptions = [...formData.options];
                                  newOptions[index] = e.target.value;
                                  setFormData({
                                    ...formData,
                                    options: newOptions,
                                  });
                                }}
                                placeholder={
                                  t("editTracker.optionPlaceholder", {
                                    index: index + 1,
                                  }) as string
                                }
                                disabled={isExistingOption}
                                className={`flex-1 px-4 py-3 rounded-xl border border-black/[0.1] dark:border-white/[0.1] bg-black/[0.05] dark:bg-white/[0.06] text-[18px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/25 focus:outline-none focus:border-[#a33fe8] transition-colors ${isExistingOption ? "opacity-50 cursor-not-allowed" : ""}`}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setFormData({
                                    ...formData,
                                    options: formData.options.filter(
                                      (_, i) => i !== index,
                                    ),
                                  })
                                }
                                disabled={isExistingOption}
                                className={`w-8 h-8 flex items-center justify-center text-red-500 hover:text-red-600 transition-colors ${isExistingOption ? "opacity-30 cursor-not-allowed" : ""}`}
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              options: [...formData.options, ""],
                            })
                          }
                          className="flex items-center gap-1.5 text-[17px] text-[#a33fe8] font-medium hover:opacity-80 transition-opacity"
                        >
                          <span>+</span> {t("editTracker.addOption")}
                        </button>
                      </div>
                      <p className="text-[15px] text-gray-400 dark:text-white/30 mt-1">
                        {t("editTracker.optionsHelpText")}
                      </p>
                    </div>
                  )}

                  <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                    <FormTextarea
                      id="description"
                      label={t("editTracker.descriptionLabel") as string}
                      value={formData.description}
                      onChange={(value) =>
                        setFormData({ ...formData, description: value })
                      }
                      placeholder={
                        t("editTracker.descriptionPlaceholder") as string
                      }
                      rows={3}
                    />
                  </div>

                  {/* Tracking Days */}
                  <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                    <label className="block text-[17px] font-semibold text-gray-800 dark:text-white/80 mb-2">
                      {t("editTracker.trackingDays")}{" "}
                      <span className="text-[15px] text-gray-400 dark:text-white/30 font-normal">
                        {t("editTracker.trackingDaysOptional")}
                      </span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {(
                        [
                          "monday",
                          "tuesday",
                          "wednesday",
                          "thursday",
                          "friday",
                          "saturday",
                          "sunday",
                        ] as DayOfWeek[]
                      ).map((day) => (
                        <label
                          key={day}
                          className="flex items-center gap-3 p-2.5 rounded-xl border border-black/[0.07] dark:border-white/[0.08] bg-white dark:bg-white/[0.04] hover:bg-black/[0.04] dark:hover:bg-white/[0.07] transition-all cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.trackingDays.includes(day)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  trackingDays: [...formData.trackingDays, day],
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  trackingDays: formData.trackingDays.filter(
                                    (d) => d !== day,
                                  ),
                                });
                              }
                            }}
                            className="w-4 h-4 accent-[#a33fe8]"
                          />
                          <span className="text-[17px] font-medium text-gray-700 dark:text-white/70 capitalize">
                            {day}
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="text-[15px] text-gray-400 dark:text-white/30 mt-1">
                      {t("editTracker.trackingDaysHelpText")}
                    </p>
                  </div>

                  {/* Pause/Resume */}
                  <div className="flex items-center justify-between p-4 bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.07] dark:border-white/[0.08] rounded-xl">
                    <div>
                      <p className="text-[17px] font-semibold text-gray-900 dark:text-white">
                        {formData.isPaused
                          ? t("editTracker.trackerPaused")
                          : t("editTracker.trackerActive")}
                      </p>
                      <p className="text-[15px] text-gray-500 dark:text-white/40">
                        {formData.isPaused
                          ? t("editTracker.resumeDescription")
                          : t("editTracker.pauseDescription")}
                      </p>
                    </div>
                    <FormButton
                      type="button"
                      variant={formData.isPaused ? "primary" : "secondary"}
                      onClick={() =>
                        setFormData({
                          ...formData,
                          isPaused: !formData.isPaused,
                        })
                      }
                      size="md"
                    >
                      {formData.isPaused
                        ? t("editTracker.resumeTracker")
                        : t("editTracker.pauseTracker")}
                    </FormButton>
                  </div>

                  <TrackerInfo />
                  <DataTypeExamples dataType={formData.dataType} />

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-black/[0.07] dark:border-white/[0.08]">
                    <div className="">
                      <FormButton
                        type="submit"
                        variant="primary"
                        loading={loading}
                        disabled={
                          !formData.name.trim() ||
                          !formData.dataType ||
                          (formData.dataType === "options" &&
                            (!formData.options ||
                              formData.options.length === 0 ||
                              formData.options.some((opt) => !opt.trim())))
                        }
                        size="lg"
                      >
                        {loading
                          ? t("editTracker.updating")
                          : t("editTracker.updateTracker")}
                      </FormButton>
                    </div>
                    <div className="">
                      <FormButton
                        type="button"
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={loading || deleteLoading}
                        size="lg"
                      >
                        {t("editTracker.cancel")}
                      </FormButton>
                    </div>
                  </div>
                </form>
              </div>

              {/* Delete Section */}
              <div className="bg-red-500/5 border border-red-500/15 rounded-2xl p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 bg-red-500/20 rounded-lg flex items-center justify-center shrink-0">
                    <span className="text-base">🗑️</span>
                  </div>
                  <div>
                    <h3 className="text-[17px] font-semibold text-red-600 dark:text-red-400 mb-0.5">
                      {t("editTracker.dangerZone")}
                    </h3>
                    <p className="text-[16px] text-red-500/80 dark:text-red-400/70">
                      {t("editTracker.deleteWarning")}
                    </p>
                  </div>
                </div>

                {!showDeleteConfirm ? (
                  <FormButton
                    onClick={() => setShowDeleteConfirm(true)}
                    variant="danger"
                    disabled={loading || deleteLoading}
                    size="md"
                  >
                    {t("editTracker.deleteTracker")}
                  </FormButton>
                ) : (
                  <div className="space-y-3">
                    <p className="text-[17px] font-semibold text-red-600 dark:text-red-400">
                      {t("editTracker.confirmDelete", {
                        trackerName: tracker.name,
                      })}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <FormButton
                        onClick={() => setShowDeleteConfirm(false)}
                        variant="secondary"
                        disabled={deleteLoading}
                        size="md"
                      >
                        {t("editTracker.cancelDelete")}
                      </FormButton>
                      <FormButton
                        onClick={handleDelete}
                        variant="danger"
                        disabled={deleteLoading}
                        size="md"
                      >
                        {deleteLoading
                          ? t("editTracker.deleting")
                          : t("editTracker.yesDelete")}
                      </FormButton>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* MOBILE BOTTOM NAV */}
          <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#f7f6f3]/95 dark:bg-[#0f0f12]/95 border-t border-black/[0.07] dark:border-white/[0.08] backdrop-blur-sm">
            <div className="flex justify-around px-2 pt-2 pb-5">
              {mobileNavLinks.map(({ path, emoji, label }) => {
                const active = isNavActive(path);
                return (
                  <button
                    key={path}
                    onClick={() => navigate(path)}
                    className="flex flex-col items-center gap-1"
                  >
                    <span className="text-[22px]">{emoji}</span>
                    <span
                      className={`text-[13px] font-medium ${active ? "text-[#f5a623]" : "text-gray-400 dark:text-white/25"}`}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>
        </main>
      </div>
    </>
  );
};

export default EditTracker;
